// Represents a single sticky column element with scroll/resize logic
class StickyColumnItem {
  constructor(settings) {
    this.settings = Object.assign(StickyColumnItem.getDefaultSettings(), settings);
    this.element = this.settings.element;
    this.parentElement = this.element.parentElement;

    this.state = 'none';
    this.lastScrollTop = window.scrollY;
    this.scrollDirection = 'down';

    this.topSentinel = null;
    this.bottomSentinel = null;

    this.resizeObserver = new ResizeObserver(this.onResize.bind(this));
    this.observedElements = new Set();

    this.init();
  }

  static getDefaultSettings() {
    return {
      offset: { top: 0, bottom: 0 },
      mobileQuery: window.matchMedia('(max-width: 768px)')
    };
  }

  // Creates an invisible element to track scroll visibility
  createSentinel(position, offset) {
    const sentinel = document.createElement('div');
    sentinel.style.position = 'absolute';
    sentinel.style[position] = `-${offset}px`;
    sentinel.style.height = '1px';
    sentinel.style.width = '100%';
    sentinel.style.pointerEvents = 'none';
    return sentinel;
  }

  // Recalculate exception heights and prepare observers
  init() {
    this.onResize();

    this.topSentinel = this.createSentinel('top', this.offsetTop);
    this.bottomSentinel = this.createSentinel('bottom', this.offsetBottom);
    this.element.appendChild(this.topSentinel);
    this.element.appendChild(this.bottomSentinel);

    this.observer = new IntersectionObserver(this.handleIntersection.bind(this));
    this.observer.observe(this.topSentinel);
    this.observer.observe(this.bottomSentinel);

    window.addEventListener('scroll', this.onScroll.bind(this));
    window.addEventListener('resize', this.onResize.bind(this));

    this.resizeObserver.observe(this.element);
    this.resizeObserver.observe(this.parentElement);

    this.observeExceptionElements();

    window.addEventListener('load', () => {
      this.stickHelper();
      this.onScroll();
    });
  }

  // Adds resize observation for elements listed in "except"
  observeExceptionElements() {
    ['top', 'bottom'].forEach(pos => {
      const configs = this.settings.selectors.except?.[pos] || [];
      configs.forEach(conf => {
        let el = null;
        if (typeof conf === 'string') {
          el = document.querySelector(conf);
        } else if (conf.closest) {
          const root = this.element.closest(conf.closest);
          el = root?.querySelector(conf.selector);
        } else {
          el = document.querySelector(conf.selector);
        }
        if (el && !this.observedElements.has(el)) {
          this.resizeObserver.observe(el);
          this.observedElements.add(el);
        }
      });
    });
  }

  handleIntersection(entries) {
    for (const entry of entries) {
      if (entry.target === this.topSentinel) this.topVisible = entry.isIntersecting;
      if (entry.target === this.bottomSentinel) this.bottomVisible = entry.isIntersecting;
    }
  }

  calculateTotalExceptionHeight(position) {
    const configs = this.settings.selectors.except[position] || [];
    return configs.reduce((sum, conf) => {
      let el = null;
      if (typeof conf === 'string') {
        el = document.querySelector(conf);
      } else if (conf.closest) {
        const root = this.element.closest(conf.closest);
        el = root?.querySelector(conf.selector);
      } else {
        el = document.querySelector(conf.selector);
      }
      return sum + (el?.offsetHeight || 0);
    }, 0);
  }

  calculateAvailableHeight() {
    this.offsetTop = this.calculateTotalExceptionHeight('top') + this.settings.offset.top;
    this.offsetBottom = this.calculateTotalExceptionHeight('bottom') + this.settings.offset.bottom;
    this.availableHeight = window.innerHeight - this.offsetTop - this.offsetBottom;
  }

  onResize() {
    if (this.settings.mobileQuery.matches) {
      this.setSticky('none');
      return;
    }

    this.blockHeight = this.element.clientHeight;
    this.calculateAvailableHeight();
    this.isBlockSmaller = this.blockHeight < this.availableHeight;

    this.setSticky(this.isBlockSmaller ? 'top' : 'relative');
    this.stickHelper();
  }

  onScroll() {
    if (this.isBlockSmaller || this.settings.mobileQuery.matches) return;

    this.scrollDirection = window.scrollY >= this.lastScrollTop ? 'down' : 'up';

    if ((this.scrollDirection === 'up' && this.state === 'bottom') ||
        (this.scrollDirection === 'down' && this.state === 'top')) {
      this.setSticky('relative');
    }

    if (this.topVisible && this.scrollDirection === 'up') {
      this.setSticky('top');
    }

    if (this.bottomVisible && this.scrollDirection === 'down' && this.state !== 'bottom') {
      this.setSticky('bottom');
    }

    this.lastScrollTop = window.scrollY;
  }

  // Forces sticky position on load if block already scrolled out
  stickHelper() {
    if (!this.topSentinel || !this.bottomSentinel) return;
    const topRect = this.topSentinel.getBoundingClientRect();
    const bottomRect = this.bottomSentinel.getBoundingClientRect();
    if (bottomRect.top < this.offsetTop || topRect.top < 0) {
      this.setSticky('bottom');
    }
  }

  // Applies sticky position or removes it
  setSticky(position) {
    if (position === 'top') {
      this.element.style.position = 'sticky';
      this.element.style.top = `${this.offsetTop}px`;
      this.state = 'top';
    } else if (position === 'bottom') {
      const offset = window.innerHeight - this.offsetBottom - this.blockHeight;
      this.element.style.position = 'sticky';
      this.element.style.top = `${offset}px`;
      this.state = 'bottom';
    } else if (position === 'relative') {
      const parentPaddingTop = parseFloat(getComputedStyle(this.parentElement).paddingTop);
      const offsetTop = this.element.offsetTop - parentPaddingTop;
      this.element.style.position = 'relative';
      this.element.style.top = `${offsetTop}px`;
      this.state = 'none';
    } else {
      this.element.style.removeProperty('position');
      this.element.style.removeProperty('top');
      this.state = 'none';
    }
  }
}

// Manages multiple sticky columns
class StickyColumn {
  constructor(settings) {
    this.settings = settings;
    this.columns = [];
  }

  // Initializes only new (unprocessed) elements
  bindNew() {
    this.getUninitialized().forEach(column => {
      const sticky = new StickyColumnItem({
        ...this.settings,
        element: column
      });
      this.columns.push(sticky);
    });
    this.columns.forEach(c => c.onResize());
  }

  getUninitialized() {
    const all = document.querySelectorAll(this.settings.selectors.column);
    const processed = this.columns.map(c => c.element);
    return Array.from(all).filter(el => !processed.includes(el));
  }
}

export default StickyColumn;
